import type { ZudokuConfig } from "zudoku";
import { NotFoundPage } from "./src/not-found-page";
import { FrontPage } from "./src/front-page";
import SwaggerM2mPage from "./src/swagger-m2m-page";
import SwaggerWebClientPage from "./src/swagger-webclient-page";
import pkg from "./package.json";
import { useAuth } from "zudoku/hooks";
import { LogInIcon } from "zudoku/icons";

const config: ZudokuConfig = {
  site: {
    title: "UtilityCloud",
    logo: {
      src: {
        light: "/utilitycloud_logo_rgb_black.png",
        dark: "/utilitycloud_logo_rgb_white.png"
      },
      alt: "UtilityCloud logo",
    },
    showPoweredBy: false,
    footer: {
      copyright: `${pkg.name} v${pkg.version} © ${new Date().getFullYear()} UtilityCloud AS. All rights reserved. Powered by Zudoku (www.zudoku.dev).`,
    },
    notFoundPage: <NotFoundPage />,
  },
  metadata: {
    title: "%s | UtilityCloud", // %s will be replaced by the page title
    favicon: "/favicon.ico",
    publisher: "UtilityCloud AS",
  },
  search: {
    type: "pagefind",
  },
  authentication: {
    type: "openid",
    issuer: "https://login.microsoftonline.com/73fbcf56-ad0e-441c-b431-2a36dc8918d7/v2.0", // UtilityCloud's Entra ID
    clientId: "9db49ce2-1dc3-41ae-b922-ee247991fef3", // app.reg for UC Developer Portal, Zudoku
    scopes: ["openid", "profile", "email"], // optional: custom scopes
    disableSignUp: true,
  },
  protectedRoutes: [
    "/uc-only/*",
    "/playground/*",
    "/apis/webclient/*",
    "/swagger/webclient/*", // note: the schema.json is still accessible
  ],
  // redirects: [{ from: "/", to: "/getting-started" }],
  navigation: [
    {
      type: "custom-page",
      path: "/",
      element: <FrontPage />,
    },
    {
      type: "category",
      label: "Documentation",
      items: [
        "getting-started",
        "core-concepts",
        "authentication",
        "environments",
        {
          type: "category",
          collapsed: false,
          label: "Common workflows",
          items: [
            "common-workflows",
            "workflows/workflow-find-customer-by-identification",
            "workflows/workflow-find-customer-by-phone-number",
            "workflows/workflow-search-customer",
            "workflows/workflow-get-invoices-for-customer",
            "workflows/workflow-get-invoice-pdf",
            "workflows/workflow-get-invoice-details-with-lines",
            "workflows/workflow-get-calculations-accounting-point",
            "workflows/workflow-get-price-information",
            "workflows/workflow-send-meter-values",
            "workflows/workflow-bulk-retrievals",
            "workflows/workflow-create-contract",
            "workflows/create-contract/workflow-create-contract-no-dh",
            "workflows/workflow-create-offer",
            "workflows/workflow-add-remove-product-contract",
            "workflows/workflow-subscribe-events",
          ]
        },
      ],
    },
    {
      type: "link",
      to: "/apis/m2m",
      label: "API Reference",
    },
    {
      type: "custom-page",
      path: "/swagger/m2m",
      element: <SwaggerM2mPage />,
      label: "Swagger",
    },
    {
      type: "link",
      to: "/apis/webclient",
      label: "WebClient API Reference",
      display: "auth", // only logged in users see this
    },
    {
      type: "custom-page",
      path: "/swagger/webclient",
      element: <SwaggerWebClientPage />,
      label: "WebClient Swagger",
      display: "auth", // only logged in users see this
    },
    // {
    //   type: "link",
    //   to: "/uc-only/hello",
    //   label: "Only auth",
    //   display: "auth", // only logged in users see this
    // },
    // {
    //   type: "link",
    //   to: "/playground/ole",
    //   label: "Only anon",
    //   display: "anon", // only logged-out users see this
    // },
  ],
  catalogs: [
    // {
    //   path: "/catalog",
    //   label: "API Catalog (foo)",
    // },
    /*
  {
    path: "/petcatalog",
    label: "API Catalog (bar)",
    // items: ["api-operational", "/petapi"],
    filterItems: (items, { auth }) => {
      return items.filter((item) => item.path == "/api-operational");
    },
  }
  */
  ],
  defaults: {
    apis: {
      // examplesLanguage: "shell", // Default language for code examples
      // supportedLanguages: [], // array of language options for code examples.
      // disablePlayground: false, // Disable the interactive API playground
      // disableSidecar: false, // Disable the sidecar completely
      // Ole: for some reason this is flagged as not valid, but it works!
      disableSecurity: false, // Disable security scheme display and playground auth (default true)
      // showVersionSelect: "if-available", // Control version selector visibility
      expandAllTags: true, // (default: true, but overridden in defaults.api above) Control initial expanded state of tag categories
      showInfoPage: true, // (default: true) Show API information page as the index route (show as Information at the top of the left column)
      schemaDownload: {
        enabled: true,  // (default: false) Enable schema download button. Show as "Download schema" button in upper right corner
        // fileName: "schema", // Set name of the schema file when downloaded
      },
    },
  },
  apis: [
    {
      type: "file",
      input: "./apis/uc-m2m-final.json",
      path: "/apis/m2m",
    },
    {
      type: "file",
      input: "./apis/uc-widget-final.json",
      path: "/apis/webclient",
    },
    // {
    //   type: "file",
    //   input: "./apis/petstore-openapi.yaml",
    //   path: "/apis/petstore",
    // },
    /*
     {
       type: "file",
       input: "./apis/servicetypes-swagger.json",
        path: "/api/servicetypes",
       // categories: label is primary grouping (upper right); tags are secondary description of the category (used in API search)
       categories: [{ label: "General", tags: ["Operational", "Dysfunctional"] }],
       options: {
         expandAllTags: true, // (default: true, but overridden in defaults.api above) Control initial expanded state of tag categories
         showInfoPage: false, // (default: true) Show API information page as the index route (show as Information at the top of the left column)
         schemaDownload: {
           enabled: true, // (default: false) Enable schema download button. Show as "Download schema" button in upper right corner
         },
       },
     },
     */
  ],
  docs: {
    files: "pages/**/*.{md,mdx}", // markdown files or directories with markdown files. Each file will be a page in the documentation section. Subdirectories will be categories.
    defaultOptions: {
      copyPage: true, // Show/hide "Copy page" button on top of each doc page
      showLastModified: false, // hide "Last modified" timestamp at the bottom of each doc page
      // no suggestEdit here, i.e. don't show "Edit this page" link for docs, since our docs are not in a GitHub repo and we don't want to direct users to a non-existent page
    },
    publishMarkdown: true, // generates .md files for each documentation page during build. Pages can then be accessed at their URL path with the .md extension appended (e.g., /docs/quickstart.md).
    llms: {
      llmsTxt: true, // generate llms.txt
      llmsTxtFull: true, // generate llms-full.txt
      includeProtected: false, // exclude protected routes
    },
  },
  //aiAssistants: false, // by default "Download schema" and "Copy page" buttons includes Use in Claude and ChatGPT 
  //aiAssistants: ["claude"], // Only show Claude
  syntaxHighlighting: {
    //  Extend default languages (bash, json, jsonc, yaml) https://zudoku.dev/docs/markdown/code-blocks#default-supported-languages
    languages: ["http"],
  },
  slots: {
    "head-navigation-end": () => <LoginButton />,
  },
  theme: {
    customCss: `
    .hidden.lg\\:flex.items-center.text-sm.gap-2:not(:has([data-authenticated])) button.group\\/button {
      display: none;
    }
  `,
  },
  mdx: {
    components: {
      LogInIcon,
    },
  },
};

const LoginButton = () => {
  const auth = useAuth();

  if (auth.isAuthenticated) {
    return <span data-authenticated="true" style={{ display: "none" }} />;
  }

  return (
    <button type="button" title="Login for UtilityCloud employees"
      onClick={() => (window.location.href = "/login")}
      className="inline-flex items-center justify-center h-9 w-9
                 rounded-lg transition-all hover:bg-muted hover:text-foreground">
      <LogInIcon size={16} />
    </button>
  );
};

export default config;
